'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import styles from './PromoCard.module.scss';

interface PromoCardProps {
  id: string;
  image?: {
    src: string;
    srcSet?: string;
    alt: string;
  };
  heading: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  description?: string;
  truncateDescription?: boolean;
  price?: {
    current: string;
    old?: string;
  };
  action?: React.ReactNode;
  testId?: string;
}

export const PromoCard: React.FC<PromoCardProps> = ({
  id,
  image,
  heading,
  headingLevel = 2,
  description,
  truncateDescription,
  price,
  action,
  testId,
}) => {
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
  const headingId = `${id}-heading`;
  const t = useTranslations('components.promoCard');

  return (
    <article className={styles.card} data-testid={testId} aria-labelledby={headingId}>
      {image && (
        <img src={image.src} srcSet={image.srcSet} alt={image.alt} className={styles.image} />
      )}
      <HeadingTag className={styles.heading} id={headingId}>
        {heading}
      </HeadingTag>
      {description && (
        <p
          className={clsx(styles.description, {
            [styles.descriptionTruncated]: truncateDescription,
          })}
        >
          {description}
        </p>
      )}
      {price && (
        <dl className={styles.price}>
          <dt className="sr-only">{t('price')}</dt>
          <dd className={styles.priceValue}>{price.current}</dd>
          {price.old && (
            <>
              <dt className="sr-only">{t('previousPrice')}</dt>
              <dd className={clsx(styles.priceValue, styles.priceOld)}>{price.old}</dd>
            </>
          )}
        </dl>
      )}
      {action && <footer className={styles.footer}>{action}</footer>}
    </article>
  );
};
